import { Box, Grid, Typography } from '@mui/material';
import { memo } from 'react';

import {
  convertElevationGrade,
  convertFencingExtent,
  convertFrontFencing,
  convertLotPosition,
  convertLotShape,
  convertLotSlope,
  convertPredominantUse,
} from '../../../api/characteristics/enumerations';
import {
  BaseLandAndFencing,
  SectionStatus,
} from '../../../api/characteristics/types';
import { SectionStatusBadge } from '../components/StatusIndicators';
import { StyledDivider } from '../styles';

interface HouseLandAndFencingSectionProps {
  formData: BaseLandAndFencing;
  sectionStatus?: SectionStatus;
}

export const HouseLandAndFencingSection = memo(
  function HouseLandAndFencingSection({
    formData,
    sectionStatus,
  }: HouseLandAndFencingSectionProps) {
    return (
      <Box>
        <Grid container spacing={2} marginBottom={3}>
          <Grid item xs={12}>
            <SectionStatusBadge status={sectionStatus} />
          </Grid>
          <Grid item xs={12}>
            <Typography fontSize={18} fontWeight={600} id="land-lote-unidade">
              Lote da unidade
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Posição
                </Typography>
                <Typography>
                  {formData.lot.position
                    ? convertLotPosition(formData.lot.position)
                    : '-'}
                </Typography>
              </Grid>
              {formData.lot.position_other && (
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Posição - Outros
                  </Typography>
                  <Typography>{formData.lot.position_other}</Typography>
                </Grid>
              )}
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Cota/Greide
                </Typography>
                <Typography>
                  {formData.lot.grade
                    ? convertElevationGrade(formData.lot.grade)
                    : '-'}
                </Typography>
              </Grid>
              {formData.lot.grade_other && (
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Cota/Greide - Outros
                  </Typography>
                  <Typography>{formData.lot.grade_other}</Typography>
                </Grid>
              )}
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Formato Aproximado
                </Typography>
                <Typography>
                  {formData.lot.shape
                    ? convertLotShape(formData.lot.shape)
                    : '-'}
                </Typography>
              </Grid>
              {formData.lot.shape_other && (
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Formato - Outros
                  </Typography>
                  <Typography>{formData.lot.shape_other}</Typography>
                </Grid>
              )}
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Inclinação
                </Typography>
                <Typography>
                  {formData.lot.slope
                    ? convertLotSlope(formData.lot.slope)
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} marginBottom={3}>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <Grid item xs={12}>
            <Typography
              fontSize={18}
              fontWeight={600}
              id="land-tipo-cercamento"
            >
              Tipo de cercamento
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Cercamento
                </Typography>
                <Typography>
                  {formData.fencing_type.fencing
                    ? convertFencingExtent(formData.fencing_type.fencing)
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Frente
                </Typography>
                <Typography>
                  {formData.fencing_type.front_fence
                    ? convertFrontFencing(formData.fencing_type.front_fence)
                    : '-'}
                </Typography>
              </Grid>
              {formData.fencing_type.front_fence_other && (
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Frente - Outros
                  </Typography>
                  <Typography>
                    {formData.fencing_type.front_fence_other}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Lateral / Fundo
                </Typography>
                <Typography>
                  {formData.fencing_type.side_back_fence
                    ? convertFrontFencing(formData.fencing_type.side_back_fence)
                    : '-'}
                </Typography>
              </Grid>
              {formData.fencing_type.side_back_fence_other && (
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Lateral / Fundo - Outros
                  </Typography>
                  <Typography>
                    {formData.fencing_type.side_back_fence_other}
                  </Typography>
                </Grid>
              )}
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Vocação Principal
                </Typography>
                <Typography>
                  {formData.fencing_type.main_vocation
                    ? convertPredominantUse(formData.fencing_type.main_vocation)
                    : '-'}
                </Typography>
              </Grid>
              {formData.fencing_type.main_vocation_other && (
                <Grid item xs={3}>
                  <Typography variant="caption" color="text.secondary">
                    Vocação Principal - Outros
                  </Typography>
                  <Typography>
                    {formData.fencing_type.main_vocation_other}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} marginBottom={3}>
          <Grid item xs={12}>
            <StyledDivider />
          </Grid>
          <Grid item xs={12}>
            <Typography fontSize={18} fontWeight={600} id="land-dados-fisicos">
              Dados Físicos
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Área (m²)
                </Typography>
                <Typography>{formData.physical_data.area_m2 ?? '-'}</Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Testada
                </Typography>
                <Typography>
                  {formData.physical_data.frontage ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Profundidade equivalente (m)
                </Typography>
                <Typography>
                  {formData.physical_data.equivalent_depth ?? '-'}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography variant="caption" color="text.secondary">
                  Lados
                </Typography>
                <Typography>{formData.physical_data.sides ?? '-'}</Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  }
);
